'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Image from 'next/image';
import { FC } from 'react';
import { TestimonialsSectionDto } from '@/components/service-pages/TestimonialsSection/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import { VIDEOS } from './slides';
import css from './style.module.scss';
import { useTestimonials } from './useTestimonials';
interface Props {
  data: TestimonialsSectionDto;
  isIndustry?: boolean;
}
const TestimonialsSection: FC<Props> = ({
  data,
  isIndustry = false
}) => {
  const {
    containerRef,
    bgRefs,
    contentRefs,
    activeIndex,
    setIsHovered,
    handleSlideClick,
    getOrderedSlides
  } = useTestimonials(data.testimonials, data.autoplayDelay);
  const shouldLoadVideos = useLazyVideoSource(containerRef);
  useViewportVideosPlayback([containerRef]);
  const activeSlide = data.testimonials[activeIndex];
  useGSAP(() => {
    const titleLines = containerRef.current?.querySelectorAll(`.${css.mainTitle} p`);
    if (titleLines?.length) gsap.set(titleLines, {
      opacity: 0,
      y: 40,
      filter: 'blur(10px)'
    });
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top 70%',
        toggleActions: 'play none none reverse'
      }
    });
    if (titleLines?.length) tl.to(titleLines, {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      duration: 1,
      stagger: 0.15,
      ease: 'power3.out'
    });
  }, {
    scope: containerRef
  });
  return <section ref={containerRef} className={clsx(css.section, 'container', isIndustry && css.industry)}>
			<Typography uppercase variant={isIndustry ? 'h2-industry' : 'h3'} tag="h2" className={css.mainTitle}>
				<StrapiRichContent isInlineContent content={data.title} />
			</Typography>

			<div className={css.backgrounds}>
				{data.testimonials.map((s, i) => <div key={s.id} ref={el => {
        bgRefs.current[i] = el;
      }} className={css.bgItem}>
						{s.cover?.url ? s.cover.mime?.startsWith('video/') ? shouldLoadVideos ? <video muted loop playsInline preload="none" src={s.cover.url} /> : null : <Image fill className={css.bgImage} src={s.cover.url} sizes="100vw" alt={s.cover.alternativeText || ''} /> : VIDEOS[s.reviewFrom] && shouldLoadVideos ? <video muted loop playsInline preload="none" src={VIDEOS[s.reviewFrom]} /> : null}
					</div>)}
			</div>

			<div className={css.content}>
				{activeSlide.title ? <Typography ref={contentRefs.title} uppercase variant={isIndustry ? 'h3-industry' : 'h5'} tag="h3" className={css.slideTitle}>
						<div className={css.quoteMark}>“</div>
						<StrapiRichContent isInlineContent content={activeSlide.title} />
					</Typography> : null}
				{activeSlide.text ? <Typography ref={contentRefs.description} variant="body-l" tag="div" className={css.description}>
						<StrapiRichContent content={activeSlide.text} />
					</Typography> : null}
				{activeSlide.companyLogo?.url ? <Image ref={contentRefs.companyLogo} className={css.companyLogo} src={activeSlide.companyLogo.url} width={activeSlide.companyLogo.width || 110} height={activeSlide.companyLogo.height || 32} alt={activeSlide.companyLogo.alternativeText || ''} /> : null}
			</div>

			<ul className={css.list} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
				{getOrderedSlides().map(({
        slide,
        originalIndex: index
      }) => <li key={slide.id} className={css.listItem}>
						<button className={clsx(css.switcherButton, index === activeIndex && css.active)} onClick={() => handleSlideClick(index)}>
							<div className={css.avatarWrapper}>
								{index === activeIndex && <svg className={css.progressSvg} viewBox="0 0 54 54">
										<circle cx="27" cy="27" r="26" pathLength="100" fill="none" stroke="var(--color-green-500)" strokeWidth="2" />
									</svg>}
								{slide.avatar?.url ? <Image className={css.slideAvatar} src={slide.avatar.url} width={slide.avatar.width || 48} height={slide.avatar.height || 48} alt={slide.name} /> : null}
							</div>
							<div className={css.slideInfo}>
								<Typography className={css.slideName} variant={isIndustry ? 'body-l' : 'h6'} tag="p">
									{slide.name}
								</Typography>
								<Typography className={css.slidePosition} variant="body-s" tag="p">
									{slide.position}
								</Typography>
							</div>
						</button>
					</li>)}
			</ul>
		</section>;
};
export default TestimonialsSection;